import React, { useState, useEffect } from "react";
import axios from "axios";
import { RiCloseLine, RiShieldUserLine } from "react-icons/ri";
import { Check, Loader2, Lock } from "lucide-react";
import toast from "react-hot-toast";

// ─── DATA ─────────────────────────────────────

const SIDEBAR_OPTIONS = [
  { label: "Dashboard", path: "/dashboard", permission: "dashboard" },
  { label: "Staff", path: "/staff", permission: "staff" },
  { label: "Category", path: "/category", permission: "category" },
  { label: "Products", path: "/product", permission: "product" },
  { label: "Notice", path: "/carrier", permission: "notice" },
  { label: "FAQ", path: "/faq", permission: "faq" },
  { label: "Blogs", path: "/blog", permission: "blog" },
];

const ALL_PERMISSIONS = [...new Set(SIDEBAR_OPTIONS.map(s => s.permission))];

// ─── COMPONENT ───────────────────────────────

export default function CreateStaffModal({ isOpen, onClose, editData = null }) {
  const isEditMode = Boolean(editData);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    roleName: "",
    permissions: [],
    sidebar: [],
  });

  // ─── INIT ───────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (editData) {
      const mappedSidebar = (editData.sidebar || [])
        .map(s => SIDEBAR_OPTIONS.find(opt => opt.path === s.path))
        .filter(Boolean);

      setForm({
        roleName: editData.name || "",
        permissions: editData.permissions || [],
        sidebar: mappedSidebar,
      });
    } else {
      setForm({ roleName: "", permissions: [], sidebar: [] });
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  // ─── HANDLERS ───────────────────────────

  const togglePerm = (perm) => {
    const has = form.permissions.includes(perm);

    const newPerms = has
      ? form.permissions.filter(p => p !== perm)
      : [...form.permissions, perm];

    // remove sidebar if permission removed
    const newSidebar = form.sidebar.filter(s =>
      newPerms.includes(s.permission)
    );

    setForm({ ...form, permissions: newPerms, sidebar: newSidebar });
  };

  const toggleSidebar = (item) => {
    if (!form.permissions.includes(item.permission)) {
      toast.error("Enable permission first");
      return;
    }

    const exists = form.sidebar.find(s => s.path === item.path);

    setForm({
      ...form,
      sidebar: exists
        ? form.sidebar.filter(s => s.path !== item.path)
        : [...form.sidebar, item],
    });
  };

  // ─── SUBMIT ─────────────────────────────

  const handleSubmit = async () => {
    if (!form.roleName || form.permissions.length === 0) {
      toast.error("Fill all required fields");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${process.env.REACT_APP_API_URL}/role/create`,
        {
          name: form.roleName,
          permissions: form.permissions,
          sidebar: form.sidebar.map(s => ({ path: s.path })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Role created 🚀");
      onClose();

    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ─── UI ────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      
      <div className="w-[720px] bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
              <RiShieldUserLine size={20} />
            </div>
            <div>
              <h2 className="font-bold text-lg">
                {isEditMode ? "Edit Role" : "Create Role"}
              </h2>
              <p className="text-xs text-gray-400">
                Manage permissions & access
              </p>
            </div>
          </div>

          <button onClick={onClose}>
            <RiCloseLine size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-6">

          {/* ROLE NAME */}
          <div>
            <label className="text-sm font-medium">Role Name</label>
            <input
              value={form.roleName}
              onChange={(e) =>
                setForm({ ...form, roleName: e.target.value })
              }
              placeholder="e.g. Manager"
              className="mt-2 w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>

          {/* PERMISSIONS */}
          <div>
            <h3 className="font-semibold mb-3">Permissions</h3>
            <div className="flex flex-wrap gap-2">
              {ALL_PERMISSIONS.map(perm => {
                const active = form.permissions.includes(perm);
                return (
                  <button
                    key={perm}
                    onClick={() => togglePerm(perm)}
                    className={`px-4 py-2 rounded-xl text-sm transition
                      ${active
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                  >
                    {perm}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SIDEBAR */}
          <div>
            <h3 className="font-semibold mb-3">Sidebar Access</h3>

            <div className="grid grid-cols-3 gap-3">
              {SIDEBAR_OPTIONS.map(item => {
                const active = form.sidebar.find(s => s.path === item.path);
                const allowed = form.permissions.includes(item.permission);

                return (
                  <button
                    key={item.path}
                    onClick={() => toggleSidebar(item)}
                    className={`p-3 rounded-xl border text-left transition
                      ${active
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-gray-50 border-gray-200"
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{item.label}</span>

                      {!allowed && <Lock size={14} className="text-gray-400" />}
                      {active && <Check size={14} className="text-green-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="p-5 border-t flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-emerald-600 text-white px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-emerald-700"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : null}
            {isEditMode ? "Save Changes" : "Create Role"}
          </button>
        </div>

      </div>
    </div>
  );
}


// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { RiCloseLine, RiShieldUserLine, RiEyeLine, RiEyeOffLine } from "react-icons/ri";
// import { Check, Loader2, Pencil } from "lucide-react";
// import toast from "react-hot-toast";


// const SIDEBAR_OPTIONS = [
//   { label: "Dashboard",        path: "/dashboard",   permission: "dashboard"   },
//   { label: "Staff",            path: "/staff",       permission: "staff"       },
//   { label: "Category",         path: "/category",    permission: "category"    },
//   { label: "Products",         path: "/product",     permission: "product"     },
//   { label: "Carrier",          path: "/carrier",     permission: "carrier"     },
//   { label: "Testimonial",      path: "/testimonial", permission: "admin"       },
//   { label: "FAQ",              path: "/faq",         permission: "admin"       },
//   { label: "Inquiries",        path: "/inquiry",     permission: "admin"       },
//   { label: "Banner",           path: "/hero",        permission: "dashboard"   },
//   { label: "Blog",             path: "/blog",        permission: "admin"       },
// ];

// const ALL_PERMISSIONS = [...new Set(SIDEBAR_OPTIONS.map((s) => s.permission))];

// const PERM_COLORS = {
//   dashboard:   { bg: "#f0fdf4", border: "#86efac", color: "#16a34a" },
//   staff:       { bg: "#fdf4ff", border: "#e879f9", color: "#a21caf" },
//   category:    { bg: "#eff6ff", border: "#93c5fd", color: "#2563eb" },
//   product:     { bg: "#fff7ed", border: "#fdba74", color: "#ea580c" },
//   carrier:     { bg: "#fdf2f8", border: "#f0abfc", color: "#c026d3" },
//   admin:       { bg: "#fef2f2", border: "#fca5a5", color: "#dc2626" },
// };

// const ROLE_PRESETS = [
//   { name: "Super Admin", permissions: ALL_PERMISSIONS,                                sidebar: SIDEBAR_OPTIONS },
//   { name: "Manager",     permissions: ["dashboard", "product", "category", "enquiry"], sidebar: SIDEBAR_OPTIONS.filter(s => ["dashboard", "product", "category", "enquiry"].includes(s.permission)) },
//   { name: "Staff",       permissions: ["dashboard"],                                   sidebar: SIDEBAR_OPTIONS.filter(s => s.permission === "dashboard") },
// ];

// // In edit mode, only show these 2 steps (no Account step)
// const CREATE_STEPS = ["Role & Access"];
// const EDIT_STEPS   = ["Role & Access"];

// // ─── Helpers ──────────────────────────────────────────────────────────────────

// function SectionHeading({ children }) {
//   return (
//     <div className="flex items-center gap-3 mt-6 mb-3">
//       <span className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
//         {children}
//       </span>
//       <div className="flex-1 h-px bg-slate-100" />
//     </div>
//   );
// }

// function Field({ label, children }) {
//   return (
//     <div className="flex flex-col gap-1.5">
//       <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
//       {children}
//     </div>
//   );
// }

// const inputCls =
//   "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 " +
//   "placeholder-slate-300 outline-none transition-all duration-150 " +
//   "focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/10";


// export default function CreateStaffModal({ isOpen, onClose, editData = null }) {
//   const isEditMode = Boolean(editData);
//   const STEPS      = isEditMode ? EDIT_STEPS : CREATE_STEPS;

//   const [step, setStep]       = useState(0);
//   const [showPw, setShowPw]   = useState(false);
//   const [loading, setLoading] = useState(false);

//   const emptyForm = {
//     roleName: "", permissions: [], sidebar: [],
//   };

//   const [form, setForm] = useState(emptyForm);

//   // ── Populate form when modal opens ──────────────────────────────
//   useEffect(() => {
//     if (!isOpen) return;

//     setStep(0);
//     setShowPw(false);

//     if (isEditMode && editData) {
//       // Map API sidebar (array of {path, _id}) back to SIDEBAR_OPTIONS shape
//       const mappedSidebar = (editData.sidebar || [])
//         .map(s => SIDEBAR_OPTIONS.find(opt => opt.path === s.path))
//         .filter(Boolean); // drop unknown paths

//       setForm({

//         roleName:    editData.name        || "",
//         permissions: editData.permissions || [],
//         sidebar:     mappedSidebar,
//       });
//     } else {
//       setForm(emptyForm);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isOpen, editData]);

//   if (!isOpen) return null;

//   // ── Handlers ────────────────────────────────────────────────────
//   const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

//   const togglePerm = (perm) => {
//     const has      = form.permissions.includes(perm);
//     const newPerms = has
//       ? form.permissions.filter(p => p !== perm)
//       : [...form.permissions, perm];
//     // auto-remove sidebar items whose permission was just un-ticked
//     const newSidebar = has
//       ? form.sidebar.filter(s => newPerms.includes(s.permission))
//       : form.sidebar;
//     setForm(f => ({ ...f, permissions: newPerms, sidebar: newSidebar }));
//   };

//   const toggleSidebar = (item) =>
//     setForm(f => ({
//       ...f,
//       sidebar: f.sidebar.find(s => s.path === item.path)
//         ? f.sidebar.filter(s => s.path !== item.path)
//         : [...f.sidebar, item],
//     }));

//   const applyPreset = (p) =>
//     setForm(f => ({ ...f, roleName: p.name, permissions: p.permissions, sidebar: p.sidebar }));

//   // ── Submit (create OR edit) ──────────────────────────────────────
//   const handleSubmit = async () => {
//     setLoading(true);
//     const token = localStorage.getItem("token");

//     try {
//       // Sidebar payload: API only needs { path }
//       const sidebarPayload = form.sidebar.map(s => ({ path: s.path }));

//       if (isEditMode) {
//         // ── EDIT: only update the role ──────────────────────────
//         await axios.put(
//           `${process.env.REACT_APP_API_URL}/role/update/${editData._id}`,
//           {
//             name:        form.roleName,
//             permissions: form.permissions,
//             sidebar:     sidebarPayload,
//           },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         toast.success("Role updated successfully");

//       } else {
//         // ── CREATE: role first, then staff user ─────────────────
//         const roleRes = await axios.post(
//           `${process.env.REACT_APP_API_URL}/role/create`,
//           {
//             name:        form.roleName,
//             permissions: form.permissions,
//             sidebar:     sidebarPayload,
//           },
//           { headers: { Authorization: `Bearer ${token}` } }
//         );

//         toast.success("Role created successfully");
//       }

//       onClose(); // parent should refetch on close

//     } catch (err) {
//       console.error(err.response?.data || err.message);
//       toast.error(err.response?.data?.message || (isEditMode ? "Error updating role" : "Error creating staff"));
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Step validation ─────────────────────────────────────────────
//   const canNext = (() => {
//     if (isEditMode) {
//       // step 0 = Role & Access, step 1 = Review
//       return step === 0 ? (form.roleName && form.permissions.length > 0) : true;
//     }
//     // create mode
//     if (step === 0) return form.name && form.email && form.password;
//     if (step === 1) return form.roleName && form.permissions.length > 0;
//     return true;
//   })();

//   // Which step index maps to which content
//   // Create: 0=Account, 1=Role&Access, 2=Review
//   // Edit:   0=Role&Access, 1=Review
//   const contentStep = isEditMode ? step + 1 : step;

//   const progressWidth = `${((step + 1) / STEPS.length) * 100}%`;

//   const avatarLetter = isEditMode
//     ? editData?.name?.[0]?.toUpperCase()
//     : form.name?.[0]?.toUpperCase();

//   return (
//     <div
//       className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
//       onClick={(e) => e.target === e.currentTarget && onClose()}
//     >
//       <div className="bg-white w-[680px] max-h-[90vh] rounded-2xl border border-slate-200 shadow-2xl shadow-slate-900/10 flex flex-col overflow-hidden">

//         {/* ── Header ──────────────────────────────────────────────── */}
//         <div className="px-8 pt-7 pb-0 flex-shrink-0 bg-white">
//           <div className="flex items-center gap-4 relative">

//             {/* Avatar */}
//             <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center font-bold text-xl flex-shrink-0 select-none
//               ${isEditMode
//                 ? "bg-gradient-to-br from-blue-100 to-indigo-200 border-indigo-300 text-indigo-700"
//                 : "bg-gradient-to-br from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-700"}`}>
//               {avatarLetter || (isEditMode ? <Pencil size={18} /> : <RiShieldUserLine size={20} />)}
//             </div>

//             {/* Title */}
//             <div className="flex-1 min-w-0">
//               <h2 className="text-[19px] font-bold text-slate-900 tracking-tight leading-tight">
//                 {isEditMode ? `Edit Role — ${editData.name}` : "Create Staff Member"}
//               </h2>
//               <p className="text-xs text-slate-400 mt-0.5">
//                 {STEPS[step]} — Step {step + 1} of {STEPS.length}
//               </p>
//             </div>

//             {/* Close */}
//             <button
//               onClick={onClose}
//               className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-all duration-150 flex-shrink-0"
//             >
//               <RiCloseLine size={16} />
//             </button>
//           </div>

//           {/* Step tabs */}
//           <div className="flex mt-6 border-b border-slate-100">
//                 <button
               
//                   className=
//                     "flex-1 flex items-center gap-2 px-4 py-3 text-[13px] font-medium border-b-2 -mb-px transition-all duration-150 border-emerald-500 text-slate-800"
               
//                 >
//                   <span className=
//                     "w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 bg-emerald-500 text-white transition-all duration-150"
//                    >
//                    <Check size={10} strokeWidth={3} /> 
//                   </span>
//                   "Role & Access"
//                 </button>
          
//           </div>
//         </div>

//         {/* ── Progress bar ────────────────────────────────────────── */}
//         <div className="h-0.5 bg-slate-100 flex-shrink-0 mx-8">
//           <div
//             className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500 ease-out"
//             style={{ width: progressWidth }}
//           />
//         </div>

//         {/* ── Body ────────────────────────────────────────────────── */}
//         <div className="px-8 py-7 overflow-y-auto flex-1">

//           {/* ── STEP: Role & Access ── */}
//           {contentStep === 1 && (
//             <div>
//               <SectionHeading>Role Configuration</SectionHeading>
//               <Field label="Role Name">
//                 <input name="roleName" value={form.roleName} onChange={set}
//                   placeholder="e.g. Content Manager" className={inputCls}
//                   disabled={isEditMode && editData?.isSystem}
//                 />
//               </Field>
//               {isEditMode && editData?.isSystem && (
//                 <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
//                   ⚠️ This is a system role. The name cannot be changed.
//                 </p>
//               )}

//               <SectionHeading>Quick Presets</SectionHeading>
//               <div className="flex flex-wrap gap-2">
//                 {ROLE_PRESETS.map(p => (
//                   <button
//                     key={p.name}
//                     onClick={() => applyPreset(p)}
//                     className="px-3.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-500 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all duration-150"
//                   >
//                     {p.name}
//                   </button>
//                 ))}
//               </div>

//               <SectionHeading>Module Permissions</SectionHeading>
//               <div className="flex flex-wrap gap-2">
//                 {ALL_PERMISSIONS.map(perm => {
//                   const c      = PERM_COLORS[perm] || PERM_COLORS.admin;
//                   const active = form.permissions.includes(perm);
//                   return (
//                     <button
//                       key={perm}
//                       onClick={() => togglePerm(perm)}
//                       style={active ? { background: c.bg, borderColor: c.border, color: c.color } : {}}
//                       className={[
//                         "flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150",
//                         active
//                           ? "shadow-sm"
//                           : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700 hover:border-slate-300",
//                       ].join(" ")}
//                     >
//                       <span
//                         className="w-2 h-2 rounded-full flex-shrink-0"
//                         style={{ background: active ? c.color : "#cbd5e1" }}
//                       />
//                       {perm.charAt(0).toUpperCase() + perm.slice(1)}
//                     </button>
//                   );
//                 })}
//               </div>

//               <SectionHeading>Sidebar Access</SectionHeading>
//               <div className="grid grid-cols-3 gap-2">
//                 {SIDEBAR_OPTIONS.map(item => {
//                   const active = !!form.sidebar.find(s => s.path === item.path);
//                   return (
//                     <button
//                       key={item.path}
//                       onClick={() => toggleSidebar(item)}
//                       className={[
//                         "flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-[13px] font-medium transition-all duration-150 text-left",
//                         active
//                           ? "bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm"
//                           : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 hover:border-slate-300",
//                       ].join(" ")}
//                     >
//                       <span className={`flex-shrink-0 transition-opacity ${active ? "opacity-100" : "opacity-40"}`} />
//                       <span className="flex-1 truncate">{item.label}</span>
//                       {active && (
//                         <span className="ml-auto flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
//                           <Check size={9} strokeWidth={3} className="text-white" />
//                         </span>
//                       )}
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>
//           )}
//         </div>

//         {/* ── Footer ──────────────────────────────────────────────── */}
//         <div className="px-8 py-5 border-t border-slate-100 bg-white flex items-center justify-between flex-shrink-0">
         

//           <button
//             disabled={!canNext || loading}
//             onClick={step < STEPS.length - 1 ? () => setStep(s => s + 1) : handleSubmit}
//             className={[
//               "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white",
//               "shadow-lg hover:-translate-y-0.5 transition-all duration-150",
//               "disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0",
//               isEditMode
//                 ? "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 hover:shadow-indigo-300"
//                 : "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200 hover:shadow-emerald-300",
//             ].join(" ")}
//           >
//             {loading ? (
//               <><Loader2 size={14} className="animate-spin" /> {isEditMode ? "Saving..." : "Creating..."}</>
//             ) : step < STEPS.length - 1 ? (
//               "Continue →"
//             ) : isEditMode ? (
//               "Save Changes"
//             ) : (
//               "Create Staff Member"
//             )}
//           </button>
//         </div>

//       </div>
//     </div>
//   );
// }